import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Drawer, Input, Select, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { MainLayout } from "../../components/layout";
import { TaskService } from "../../services";
import { Task, TaskQueryParams, TaskStatus, TaskType, TaskTypeOption } from "../../types/task";
import ErrorHandler from "../../utils/errorHandler";
import {
  Callout,
  DrawerFooter,
  EmptyState,
  Icon,
  MetricStrip,
  Pager,
  StatusBadge,
  Surface,
  TASK_STATUS,
  TASK_TYPE_LABEL,
  ToolbarGroup,
  formatNumber,
  formatTime,
} from "../../components/ui";
import "./task.scss";

const { RangePicker } = DatePicker;

type StatusKey = "all" | TaskStatus;
type Filters = { phone: string; taskType?: string; range: [Dayjs, Dayjs] | null };
type Counts = Partial<Record<StatusKey, number>>;

const METRIC_STATUSES: TaskStatus[] = ["running", "completed", "failed"];

const statusBadge = (status: string) => {
  const hit = TASK_STATUS[status];
  return <StatusBadge tone={hit ? hit[1] : "ink"}>{hit ? hit[0] : status}</StatusBadge>;
};

const TaskManagement: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<Counts>({});
  const [typeOptions, setTypeOptions] = useState<TaskTypeOption[]>([]);
  const [phoneInput, setPhoneInput] = useState("");
  const [filters, setFilters] = useState<Filters>({ phone: "", range: null });
  const [status, setStatus] = useState<StatusKey>("all");
  const [page, setPage] = useState({ current: 1, size: 20 });
  const [detail, setDetail] = useState<Task | null>(null);

  const baseParams = useCallback((f: Filters): TaskQueryParams => {
    const params: TaskQueryParams = {};
    if (f.phone) params.phone = f.phone;
    if (f.taskType) params.taskType = f.taskType as TaskType;
    if (f.range) {
      params.startDate = f.range[0].format("YYYY-MM-DD");
      params.endDate = f.range[1].format("YYYY-MM-DD");
    }
    return params;
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params: TaskQueryParams = { ...baseParams(filters), page: page.current, limit: page.size };
      if (status !== "all") params.status = status;
      const response = await TaskService.getTaskList(params);
      if (response.code === 0 && response.data) {
        setTasks(response.data.list || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.msg || "获取任务列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [baseParams, filters, page, status]);

  // 指标条：按当前筛选条件分别取各状态总数
  const loadCounts = useCallback(async () => {
    const keys: StatusKey[] = ["all", ...METRIC_STATUSES];
    const results = await Promise.all(
      keys.map((key) =>
        TaskService.getTaskList({ ...baseParams(filters), page: 1, limit: 1, ...(key === "all" ? {} : { status: key }) }).catch(() => null)
      )
    );
    const next: Counts = {};
    results.forEach((res, index) => {
      if (res && res.code === 0 && res.data) next[keys[index]] = res.data.total || 0;
    });
    setCounts(next);
  }, [baseParams, filters]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    TaskService.getTaskTypeList()
      .then((res) => {
        if (res.code === 0 && res.data) setTypeOptions(res.data.types || []);
      })
      .catch(() => undefined);
  }, []);

  const typeLabel = useCallback(
    (type: string) => TASK_TYPE_LABEL[type] || typeOptions.find((option) => option.value === type)?.label || type,
    [typeOptions]
  );

  const typeSelectOptions = useMemo(() => {
    const values = new Set<string>([...typeOptions.map((option) => option.value), ...Object.keys(TASK_TYPE_LABEL).filter((key) => key === "discount_70")]);
    return Array.from(values).map((value) => ({ value, label: typeLabel(value) }));
  }, [typeOptions, typeLabel]);

  const updateFilters = (patch: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const resetFilters = () => {
    setPhoneInput("");
    setFilters({ phone: "", range: null });
    setStatus("all");
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const filtered = !!(filters.phone || filters.taskType || filters.range || status !== "all");

  const columns: ColumnsType<Task> = [
    { title: "任务 ID", dataIndex: "id", key: "id", width: 110, render: (id: number) => <span className="mono">#{id}</span> },
    {
      title: "账号",
      key: "account",
      width: 170,
      render: (_, record) => (
        <span className="ddp-two-line">
          <span className="mono">{record.account?.phone || record.phone || "—"}</span>
          {record.account?.username && <small>{record.account.username}</small>}
        </span>
      ),
    },
    { title: "任务类型", dataIndex: "taskType", key: "taskType", render: (type: string) => typeLabel(type) },
    { title: "状态", dataIndex: "status", key: "status", width: 120, render: statusBadge },
    {
      title: "消耗算力",
      key: "cost",
      width: 110,
      align: "right",
      render: (_, record) =>
        record.tokenLog?.operationAmount ? <span className="mono">{formatNumber(record.tokenLog.operationAmount)}</span> : <span className="text-faint">—</span>,
    },
    { title: "创建时间", dataIndex: "createdAt", key: "createdAt", width: 170, render: (time: string) => <span className="mono text-muted">{formatTime(time, true)}</span> },
    { title: "更新时间", dataIndex: "updatedAt", key: "updatedAt", width: 170, render: (time: string) => <span className="mono text-muted">{formatTime(time, true)}</span> },
    {
      title: "操作",
      key: "action",
      width: 80,
      align: "right",
      fixed: "right",
      render: (_, record) => (
        <button type="button" className="ddp-link" onClick={() => setDetail(record)}>
          详情
        </button>
      ),
    },
  ];

  const detailError: string | undefined = detail ? (detail as unknown as { errorMsg?: string }).errorMsg : undefined;
  const ext = detail?.ext && Object.keys(detail.ext).length > 0 ? detail.ext : null;

  const copyId = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(String(detail.id));
      message.success("任务 ID 已复制");
    } catch {
      message.error("复制失败，请手动复制");
    }
  };

  return (
    <MainLayout>
      <MetricStrip<StatusKey>
        active={status}
        onSelect={(key) => {
          setStatus(key);
          setPage((prev) => ({ ...prev, current: 1 }));
        }}
        cells={[
          { key: "all", label: "全部任务", value: formatNumber(counts.all), note: filtered && status === "all" ? "当前筛选" : undefined },
          { key: "running", label: "执行中", value: formatNumber(counts.running), dot: "var(--indigo-600)" },
          { key: "completed", label: "已完成", value: formatNumber(counts.completed), dot: "var(--sage-600)" },
          { key: "failed", label: "失败", value: formatNumber(counts.failed), dot: "var(--vermilion-600)", note: counts.failed ? "需关注" : undefined },
        ]}
      />
      <Surface
        toolbar={
          <>
            <ToolbarGroup>
              <Input
                className="task-page__phone"
                allowClear
                prefix={<Icon name="search" />}
                placeholder="账号手机号"
                aria-label="按手机号筛选"
                value={phoneInput}
                onChange={(e) => {
                  setPhoneInput(e.target.value);
                  if (!e.target.value && filters.phone) updateFilters({ phone: "" });
                }}
                onPressEnter={() => updateFilters({ phone: phoneInput.trim() })}
              />
              <Select
                className="task-page__type"
                allowClear
                placeholder="全部类型"
                aria-label="任务类型"
                value={filters.taskType}
                options={typeSelectOptions}
                onChange={(value) => updateFilters({ taskType: value })}
              />
              <Select
                className="task-page__status"
                aria-label="任务状态"
                value={status}
                onChange={(value: StatusKey) => {
                  setStatus(value);
                  setPage((prev) => ({ ...prev, current: 1 }));
                }}
                options={[
                  { value: "all", label: "全部状态" },
                  ...(["pending", "running", "completed", "failed", "expired"] as TaskStatus[]).map((key) => ({ value: key, label: TASK_STATUS[key][0] })),
                ]}
              />
              <RangePicker aria-label="创建时间范围" value={filters.range} onChange={(value) => updateFilters({ range: value && value[0] && value[1] ? [value[0], value[1]] : null })} />
            </ToolbarGroup>
            <ToolbarGroup>
              <Button type="text" onClick={resetFilters} disabled={!filtered}>
                重置
              </Button>
            </ToolbarGroup>
          </>
        }
        footer={<Pager total={total} current={page.current} pageSize={page.size} onChange={(current, size) => setPage({ current, size })} />}
      >
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1100 }}
          rowClassName={(record) => (detail?.id === record.id ? "ant-table-row-selected" : "")}
          locale={{
            emptyText: (
              <EmptyState
                icon="tasks"
                title={filtered ? "没有符合条件的任务" : "还没有任务"}
                desc={filtered ? "调整手机号、类型、状态或时间范围后再试。" : "商家在桌面端或插件发起任务后，会出现在这里。"}
                action={filtered ? <Button onClick={resetFilters}>清空筛选</Button> : undefined}
              />
            ),
          }}
        />
      </Surface>

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={520}
        rootClassName="ddp-drawer"
        title={
          detail && (
            <span className="ddp-drawer-title">
              <span className="ddp-drawer-title__row">
                任务详情 <span className="mono task-page__id">#{detail.id}</span> {statusBadge(detail.status)}
              </span>
              <span className="ddp-drawer-title__sub">
                {typeLabel(detail.taskType)} · {[detail.account?.username, detail.account?.phone || detail.phone].filter(Boolean).join(" ")}
              </span>
            </span>
          )
        }
        footer={
          <DrawerFooter>
            <Button icon={<Icon name="copy" />} onClick={copyId}>
              复制任务 ID
            </Button>
            <Button type="primary" className="ddp-btn-dark" onClick={() => setDetail(null)}>
              关闭
            </Button>
          </DrawerFooter>
        }
      >
        {detail && (
          <div className="task-page__detail">
            {detail.status === "failed" && (
              <Callout tone="ver" title="执行失败">
                {detailError || "服务端未返回失败原因。"} 失败的任务不扣除算力。
              </Callout>
            )}
            {(detail.status as string) === "expired" && (
              <Callout tone="ink" icon="clock" title="已过期">
                任务执行超过 60 分钟未完成，已自动结束，未扣除算力。
              </Callout>
            )}
            <dl className="ddp-dl">
              <div>
                <dt>任务类型</dt>
                <dd>{typeLabel(detail.taskType)}</dd>
              </div>
              <div>
                <dt>消耗算力</dt>
                <dd>{detail.tokenLog?.operationAmount ? `${formatNumber(detail.tokenLog.operationAmount)} 算力` : "无消耗"}</dd>
              </div>
              <div>
                <dt>开始时间</dt>
                <dd className="mono">{formatTime(detail.startTime, true)}</dd>
              </div>
              <div>
                <dt>结束时间</dt>
                <dd className="mono">{formatTime(detail.endTime, true)}</dd>
              </div>
              <div>
                <dt>创建时间</dt>
                <dd className="mono">{formatTime(detail.createdAt, true)}</dd>
              </div>
              <div>
                <dt>更新时间</dt>
                <dd className="mono">{formatTime(detail.updatedAt, true)}</dd>
              </div>
              {detail.remark && (
                <div className="is-wide">
                  <dt>备注</dt>
                  <dd>{detail.remark}</dd>
                </div>
              )}
            </dl>
            {ext && (
              <section>
                <h3 className="ddp-section-title">扩展信息</h3>
                <table className="task-page__ext">
                  <tbody>
                    {Object.entries(ext).map(([key, value]) => (
                      <tr key={key}>
                        <th scope="row">{key}</th>
                        <td>{typeof value === "object" && value !== null ? <pre>{JSON.stringify(value, null, 2)}</pre> : <span className="mono">{String(value)}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>
        )}
      </Drawer>
    </MainLayout>
  );
};

export default TaskManagement;
